import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ArrowLeft, PiggyBank } from "lucide-react";
import { useSession } from "@/hooks/use-session";
import { supabase } from "@/lib/supabase";
import { formatMoney } from "@/lib/currency";
import { SkeletonLoader, EmptyState } from "@/components/system";
import { Field } from "@/routes/FinanceAccounts";

type Goal = {
  id: string;
  display_name: string;
  target_amount: number;
  currency: string;
  due_date: string | null;
  account_id: string | null;
  achieved: boolean;
  notes: string | null;
};

export function FinanceSavings() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);

  const goals = useQuery({
    queryKey: ["finance_savings", userId],
    enabled: Boolean(userId),
    queryFn: async () => {
      const { data, error } = await (supabase as never as { from: (t: string) => any })
        .from("finance_savings_goal")
        .select("*")
        .eq("user_id", userId!)
        .order("due_date", { ascending: true, nullsFirst: false });
      if (error) throw error;
      return (data ?? []) as Goal[];
    },
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-6)" }}>
      <header style={{ display: "flex", alignItems: "baseline", gap: "var(--space-3)", flexWrap: "wrap" }}>
        <Link to="/finance" style={{ color: "var(--fg-3)", textDecoration: "none" }}>
          <ArrowLeft size={16} style={{ verticalAlign: "middle" }} /> Finance
        </Link>
        <h1 style={{ margin: 0, fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: 28 }}>Savings goals</h1>
        <div style={{ flex: 1 }} />
        <button
          onClick={() => setAdding(true)}
          style={{
            background: "var(--accent)",
            color: "var(--accent-on)",
            padding: "10px 16px",
            borderRadius: 10,
            border: "none",
            fontSize: "var(--type-body-s)",
            cursor: "pointer",
          }}
        >
          + New goal
        </button>
      </header>

      {goals.isLoading ? (
        <SkeletonLoader variant="card" height={140} />
      ) : (goals.data ?? []).length === 0 ? (
        <EmptyState
          headline="No savings goals yet."
          sub="Add a goal — e.g. 'Boston deposit · CHF 30k by Jul 1'."
        />
      ) : (
        <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}>
          {(goals.data ?? []).map((g) => (
            <article
              key={g.id}
              style={{
                padding: "var(--space-4)",
                border: "1px solid var(--hair)",
                borderRadius: "var(--radius-card)",
                background: "var(--bg-2)",
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <header style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 6 }}>
                <strong style={{ fontWeight: 500 }}>{g.display_name}</strong>
                <PiggyBank size={16} color="var(--accent)" />
              </header>
              <div className="mono" style={{ fontSize: 22, fontWeight: 500 }}>
                {formatMoney(g.target_amount, g.currency)}
              </div>
              {g.due_date && (
                <div style={{ fontSize: 12, color: "var(--fg-3)" }}>
                  by {new Date(g.due_date).toLocaleDateString(undefined, { dateStyle: "medium" })}
                </div>
              )}
              {g.notes && <div style={{ fontSize: 12, color: "var(--fg-2)" }}>{g.notes}</div>}
            </article>
          ))}
        </div>
      )}

      {adding && (
        <NewGoalSheet userId={userId!} onClose={() => setAdding(false)} onSaved={() => {
          queryClient.invalidateQueries({ queryKey: ["finance_savings", userId] });
          setAdding(false);
        }} />
      )}
    </div>
  );
}

function NewGoalSheet({ userId, onClose, onSaved }: { userId: string; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState("");
  const [target, setTarget] = useState("");
  const [currency, setCurrency] = useState("CHF");
  const [due, setDue] = useState("");
  const [notes, setNotes] = useState("");

  const save = useMutation({
    mutationFn: async () => {
      const { error } = await (supabase as never as { from: (t: string) => any }).from("finance_savings_goal").insert({
        user_id: userId,
        display_name: name.trim() || "Goal",
        target_amount: Number(target),
        currency,
        due_date: due || null,
        notes: notes.trim() || null,
      });
      if (error) throw error;
    },
    onSuccess: onSaved,
  });

  return (
    <>
      <div onClick={onClose} style={overlayStyle} />
      <aside style={sheetStyle}>
        <h2 style={{ margin: "0 0 16px 0", fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: 22 }}>
          New savings goal
        </h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Field label="Name">
            <input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} placeholder="Boston deposit" />
          </Field>
          <Field label="Target amount">
            <div style={{ display: "flex", gap: 8 }}>
              <input
                type="number"
                inputMode="decimal"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                style={{ ...inputStyle, flex: 1 }}
                placeholder="30000"
              />
              <select value={currency} onChange={(e) => setCurrency(e.target.value)} style={{ ...inputStyle, width: 110 }}>
                {["CHF", "EUR", "USD", "GBP"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </Field>
          <Field label="Due date">
            <input type="date" value={due} onChange={(e) => setDue(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Notes">
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} style={{ ...inputStyle, minHeight: 60 }} />
          </Field>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={onClose} style={ghostBtn}>Cancel</button>
            <button onClick={() => save.mutate()} disabled={!target || save.isPending} style={addBtn}>
              {save.isPending ? "Saving…" : "Save goal"}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}

const inputStyle: React.CSSProperties = {
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid var(--hair-2)",
  background: "var(--bg)",
  color: "var(--fg)",
  fontSize: "var(--type-body-s)",
  fontFamily: "inherit",
  width: "100%",
  boxSizing: "border-box",
};

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.4)",
  zIndex: 50,
};

const sheetStyle: React.CSSProperties = {
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  maxHeight: "92vh",
  overflowY: "auto",
  background: "var(--bg-2)",
  borderTop: "1px solid var(--hair)",
  borderTopLeftRadius: 18,
  borderTopRightRadius: 18,
  padding: "var(--space-5)",
  paddingBottom: "calc(var(--space-5) + env(safe-area-inset-bottom))",
  zIndex: 51,
  width: "100%",
  maxWidth: 560,
  margin: "0 auto",
};

const addBtn: React.CSSProperties = {
  background: "var(--accent)",
  color: "var(--accent-on)",
  padding: "10px 16px",
  borderRadius: 10,
  border: "none",
  fontSize: "var(--type-body-s)",
  cursor: "pointer",
  flex: 1,
};

const ghostBtn: React.CSSProperties = {
  background: "transparent",
  color: "var(--fg)",
  padding: "10px 16px",
  borderRadius: 10,
  border: "1px solid var(--hair-2)",
  fontSize: "var(--type-body-s)",
  cursor: "pointer",
};
